import { CombinedGraphQLErrors, ServerError } from '@apollo/client/errors'
import { TFunction } from 'i18next'

// Intentionally use `console` here rather than the winston-based loggerServer:
// this module is imported by many client components, and pulling winston into
// the client bundle added ~393 kB of Node polyfills.
// - On the client: datadogLogs is initialized with forwardErrorsToLogs: true,
//   so console.error/warn calls are still captured.
// - On the server: console.* writes to stdout which server logs already capture.
const logger = console

export enum ErrorCode {
  GraphqlParseError = 'GRAPHQL_PARSE_FAILED',
  GraphqlValidationFailed = 'GRAPHQL_VALIDATION_FAILED',
  PersistedQueryNotFound = 'PERSISTED_QUERY_NOT_FOUND',
  PersistedQueryNotSupported = 'PERSISTED_QUERY_NOT_SUPPORTED',

  BadUserInput = 'BAD_USER_INPUT',
  Unauthenticated = 'UNAUTHENTICATED',
  Forbidden = 'FORBIDDEN',
  InternalServerError = 'INTERNAL_SERVER_ERROR'
}

export type GraphQlError = {
  message: string
  locations?: ReadonlyArray<{ line: number; column: number }>
  path?: ReadonlyArray<string | number>
  extensions?: {
    code?: ErrorCode
    // Custom fields the backend attaches to deliberate errors. Named here so
    // consumers read them type-safely instead of casting `extensions`.
    message?: string // user-safe copy on saveLocation BAD_USER_INPUT
    errorStep?: string // AddPost step the validation error belongs to
    missingFields?: string[] // KYC fields still required
    emailAlreadyInUse?: boolean // KYC contact email belongs to another account
    exception?: {
      stacktrace?: string[]
    }
  } & { [key: string]: unknown }
}

export type ParsedError = {
  isNetworkError: boolean
  code: ErrorCode | undefined
  extensions:
    | (GraphQlError['extensions'] & {
        error?: { code?: string; message?: string } & Record<string, unknown>
      })
    | undefined
}

/**
 * Codes that represent EXPECTED outcomes (user input, permission, auth) rather
 * than system failures. They log at `warn` level so they don't trip the
 * Datadog `logger.error` alerts that page the team. Reused by the
 * `useErrorSnackbar` hook to pick its log level.
 */
export const EXPECTED_ERROR_CODES = [
  ErrorCode.BadUserInput,
  ErrorCode.Forbidden,
  ErrorCode.Unauthenticated
]

export function isExpectedErrorCode(code: ErrorCode | undefined): boolean {
  return !!code && EXPECTED_ERROR_CODES.includes(code)
}

export function parseRequestError(error: unknown): ParsedError {
  // AC4: GraphQL errors are CombinedGraphQLErrors instances
  if (CombinedGraphQLErrors.is(error)) {
    const firstError = error.errors[0]
    const code = firstError?.extensions?.code as ErrorCode | undefined
    return {
      isNetworkError: false,
      code,
      extensions: firstError?.extensions as GraphQlError['extensions']
    }
  }

  // AC4: Server/network errors are ServerError instances
  if (ServerError.is(error)) {
    return { isNetworkError: true, code: undefined, extensions: undefined }
  }

  // Fallback for unknown error shapes
  return { isNetworkError: false, code: undefined, extensions: undefined }
}

export function processRequestError(
  error: unknown,
  message: string
): Omit<ParsedError, 'isNetworkError'> {
  const { isNetworkError, code, extensions } = parseRequestError(error)

  const level = isExpectedErrorCode(code) ? logger.warn : logger.error
  level(message, error)

  if (isNetworkError) {
    throw new Error(`Network Error: ${message}`)
  }

  if (code === ErrorCode.InternalServerError) {
    throw new Error(message)
  }

  return { code, extensions }
}

/**
 * What a user-facing error snackbar should render.
 * `variant` distinguishes "you can fix this" (warning, amber) from
 * "something broke" (error, red).
 */
export type ErrorDisplay = {
  message: string
  variant: 'error' | 'warning'
}

/**
 * The raw, untranslated message an error carries. Used (a) as the verbatim
 * message for BAD_USER_INPUT when no custom `extensions.message` is present,
 * and (b) as the admin-only technical suffix on friendly/generic cases.
 */
export function getRawErrorMessage(error: unknown): string | undefined {
  if (CombinedGraphQLErrors.is(error)) return error.errors[0]?.message
  if (error instanceof Error) return error.message
  return undefined
}

/**
 * Map a *parsed* error to the message + variant we show the user. Pure — no
 * Apollo guards, no React — so it's trivially unit-testable with plain objects.
 *
 * Policy (the "smart map"):
 * - BAD_USER_INPUT  → the server's deliberate, user-safe message verbatim
 *                     (extensions.message ?? top-level message), amber warning.
 *                     The backend's formatError only lets EXT codes through with
 *                     real copy; INTERNAL/DATASOURCE are already flattened to a
 *                     generic string before they leave the server.
 * - FORBIDDEN       → friendly localized copy (raw English is safe but useless).
 * - UNAUTHENTICATED → friendly "session expired" copy.
 * - network / else  → generic.
 *
 * Admins additionally get a `· [CODE] rawMessage` suffix on the
 * friendly/generic cases so they can self-diagnose without digging in Datadog.
 * Renters never see it, and we never render the `extensions` blob (its
 * stacktrace is not stripped server-side for external codes).
 */
export function getDisplayMessageFromParsed(
  parsed: ParsedError,
  rawMessage: string | undefined,
  { t, isAdmin = false }: { t: TFunction<'common'>; isAdmin?: boolean }
): ErrorDisplay {
  const { isNetworkError, code, extensions } = parsed

  if (code === ErrorCode.BadUserInput) {
    const backendMessage =
      typeof extensions?.message === 'string' && extensions.message
        ? extensions.message
        : rawMessage
    if (backendMessage) {
      return { message: backendMessage, variant: 'warning' }
    }
    return { message: t('ERRORS.GENERIC'), variant: 'error' }
  }

  const adminSuffix =
    isAdmin && rawMessage ? ` · [${code ?? 'ERROR'}] ${rawMessage}` : ''

  if (code === ErrorCode.Forbidden) {
    return {
      message: `${t('ERRORS.FORBIDDEN')}${adminSuffix}`,
      variant: 'error'
    }
  }

  if (code === ErrorCode.Unauthenticated) {
    return {
      message: `${t('ERRORS.SESSION_EXPIRED')}${adminSuffix}`,
      variant: 'error'
    }
  }

  if (isNetworkError) {
    return { message: `${t('ERRORS.NETWORK')}${adminSuffix}`, variant: 'error' }
  }

  // Internal / datasource / unknown — the server already replaced the real
  // message with a safe generic before it reached us.
  return { message: `${t('ERRORS.GENERIC')}${adminSuffix}`, variant: 'error' }
}

/**
 * Map any thrown error to the message + variant we show the user. Thin wrapper
 * over the pure core — call this from `onError` callbacks (or via the
 * `useErrorSnackbar` hook, which also wires the snackbar + logging).
 */
export function getDisplayMessage(
  error: unknown,
  opts: { t: TFunction<'common'>; isAdmin?: boolean }
): ErrorDisplay {
  return getDisplayMessageFromParsed(
    parseRequestError(error),
    getRawErrorMessage(error),
    opts
  )
}
